// Task-row controls in the Priority column: the P1/P2/P3 priority badge
// and the stop-sign close/reopen toggle. Both PUT to /tasks/:id and
// update the UI optimistically, reverting on error.
//
// Wired by app/views/tasks/_task.html.haml; styles for `.priority-badge`
// and `.task-stop` live in app/assets/tailwind/application.css.

import { Controller } from "@hotwired/stimulus"

export default class extends Controller<HTMLElement> {
  static targets = ["priority", "stop"]
  static values = { url: String }

  declare readonly priorityTarget: HTMLElement
  declare readonly stopTarget: HTMLElement
  declare readonly urlValue: string

  // Priority badge — cycle 0 → 1 → 2 → 3 → 0.
  cyclePriority(): void {
    if (this.element.classList.contains("is-pending")) return
    const current = parseInt(this.priorityTarget.dataset.priority ?? "0", 10)
    const next = (current + 1) % 4
    this.renderPriority(next)
    this.submit("priority", String(next), () => this.renderPriority(current))
  }

  // Stop sign — toggle the task's closed state.
  toggleClosed(): void {
    if (this.element.classList.contains("is-pending")) return
    const current = this.stopTarget.dataset.closed === "true"
    const next = !current
    this.renderClosed(next)
    this.submit("closed", String(next), () => this.renderClosed(current))
  }

  // ---- rendering ----------------------------------------------------

  private renderPriority(priority: number): void {
    this.priorityTarget.dataset.priority = String(priority)
    this.priorityTarget.textContent = priority === 0 ? "—" : `P${priority}`
    this.priorityTarget.title = priority === 0
      ? "No priority — tap to set"
      : `Priority ${priority} — tap to cycle`
  }

  private renderClosed(closed: boolean): void {
    this.stopTarget.dataset.closed = String(closed)
    this.stopTarget.title = closed ? "Closed — tap to reopen" : "Close task"
    this.stopTarget.setAttribute("aria-label", closed ? "Reopen task" : "Close task")
    // The row carries `.closed-task` for its dimmed/struck styling.
    this.element.closest("tr")?.classList.toggle("closed-task", closed)
  }

  // ---- persistence --------------------------------------------------

  // PUTs task[<field>]=<value>; runs `revert` on failure.
  private async submit(field: string, value: string, revert: () => void): Promise<void> {
    this.element.classList.add("is-pending")
    const csrf =
      document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? ""
    const fd = new FormData()
    fd.append("_method", "put")
    fd.append("priority_submit", "1")
    fd.append(`task[${field}]`, value)
    fd.append("authenticity_token", csrf)

    try {
      const res = await fetch(this.urlValue, {
        method: "POST",
        body: fd,
        credentials: "same-origin",
        headers: { "X-Requested-With": "XMLHttpRequest", Accept: "application/json" },
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
    } catch (_e) {
      revert()
      window.alert("Could not update the task. Please try again.")
    } finally {
      this.element.classList.remove("is-pending")
    }
  }
}
